import {
  FetchArgs,
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  BaseQueryFn,
} from "@reduxjs/toolkit/query/react";
import { addBreadcrumb, captureException } from "../sentry.config";
import { toast } from "../components/Toast/toast";

interface CustomFetchBaseQueryArgs extends FetchArgs {
  customRedirect?: boolean;
}

/**
 * Cricket Base Query - No authentication required
 * This is a simplified version of baseQuery without token handling
 */
const cricketBaseQuery = fetchBaseQuery({
  baseUrl: 'https://cricket.oogw.ai',
  // No prepareHeaders - no authentication needed for Cricket
});

// Custom fetch base query for Cricket without auth
const customCricketFetchBaseQuery: BaseQueryFn<
  string | CustomFetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError,
  {},
  FetchBaseQueryMeta
> = async (args: any, api, extraOptions) => {
  let result = await cricketBaseQuery(args, api, extraOptions);

  // Check if response is HTML instead of JSON
  if (result?.data && typeof result.data === 'string') {
    const isHTML = /<[^>]+>/.test(result.data as string);
    if (isHTML) {
      addBreadcrumb({
        message: 'Received HTML response instead of JSON',
        category: 'api',
        level: 'error',
        data: {
          url: typeof args === 'string' ? args : args.url,
          responseSnippet: (result.data as string).substring(0, 200),
        },
      });
      captureException(new Error('HTML response instead of JSON'), {
        url: typeof args === 'string' ? args : args.url,
        response: (result.data as string).substring(0, 500),
      });
      toast.error("Something went wrong. Please try again.", {
        toastId: "html-response-error",
      });
      return {
        error: {
          status: 'PARSING_ERROR',
          error: 'Received HTML response instead of JSON',
          data: result.data
        } as FetchBaseQueryError
      };
    }
  }

  // Handle errors without redirecting to login (no auth for Cricket)
  if (result?.error) {
    if (result.error.status === 'PARSING_ERROR') {
      // toast.error("Something went wrong. Please try again.", {
      //   toastId: "parsing-error",
      // });
    }

    addBreadcrumb({
      message: 'Cricket API request failed',
      category: 'api',
      level: 'error',
      data: {
        url: typeof args === 'string' ? args : args.url,
        status: result.error.status,
        error: result.error,
      },
    });

    const errorMessage = (result.error as any)?.data?.message
      || (result.error as any)?.data?.error
      || (result.error as any)?.error
      || `API Error: ${result.error.status}`;
    const error = new Error(errorMessage);
    error.name = 'CricketAPIError';

    captureException(error, {
      url: typeof args === 'string' ? args : args.url,
      status: result.error.status,
      errorData: result.error,
    });
  }

  return result;
};

// Cricket API Slice - No authentication required
export const cricketApi = createApi({
  reducerPath: "cricketApiReducer",
  baseQuery: customCricketFetchBaseQuery,
  endpoints: (build) => ({
    // Get Cricket chat history
    getCricketChatHistory: build.query({
      query: (chatId: string) => ({
        url: `/api/v1/get-conversation-history-demo?chat_id=${chatId || ''}`,
      }),
    }),

    // Ask Cricket question
    askCricketQuestion: build.query({
      query: ({ question, chat_id }: { question: string; chat_id: string }) => ({
        url: `/api/v1/ask-google-anaytics-question-demo?question=${encodeURIComponent(question)}&chat_id=${chat_id || ''}`,
      }),
    }),

    // Delete Cricket chat history
    deleteCricketChatHistory: build.mutation({
      query: (payload: { chat_id: string }) => ({
        url: `/api/v1/delete-chat-id?chat_id=${payload.chat_id || ''}`,
        method: "DELETE",
        body: payload,
      }),
    }),

    // Cricket thumbs up/down feedback
    cricketThumbsUpStatus: build.mutation({
      query: (payload: {
        chat_id: string | null;
        timestamp: string;
        thumbs_up: number;
        mode: 'add' | 'edit';
        comment?: string;
      }) => ({
        url: `/api/v1/update-chat-thumbs?chat_id=${payload.chat_id || ''}`,
        method: "POST",
        body: payload,
      }),
    }),
  }),
  tagTypes: ["cricketChat"],
});

// Export hooks for Cricket endpoints
export const {
  useLazyGetCricketChatHistoryQuery,
  useLazyAskCricketQuestionQuery,
  useDeleteCricketChatHistoryMutation,
  useCricketThumbsUpStatusMutation,
} = cricketApi;
